
import React, { useState, useEffect } from 'react'
import { X, Star } from 'lucide-react'

interface FornecedorFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  initialData?: any
  title: string
}

const FornecedorForm: React.FC<FornecedorFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title
}) => {
  const [formData, setFormData] = useState({
    nome: '',
    segmento: '',
    contato: '',
    endereco: '',
    cnpj: '',
    confiabilidade: 3,
    tempoEntregaMedio: 7,
    precoMedio: 0,
    ativo: true,
    bloqueado: false,
    motivoBloqueio: '',
    observacoes: ''
  })

  // Initialize form with existing data
  useEffect(() => {
    if (initialData) {
      setFormData({
        nome: initialData.nome || '',
        segmento: initialData.segmento || '',
        contato: initialData.contato || '',
        endereco: initialData.endereco || '',
        cnpj: initialData.cnpj || '',
        confiabilidade: initialData.confiabilidade || 3,
        tempoEntregaMedio: initialData.tempoEntregaMedio || 7,
        precoMedio: initialData.precoMedio || 0,
        ativo: initialData.ativo !== undefined ? initialData.ativo : true,
        bloqueado: initialData.bloqueado || false,
        motivoBloqueio: initialData.motivoBloqueio || '',
        observacoes: initialData.observacoes || ''
      })
    }
  }, [initialData])

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
    onClose()
  }

  // Render star rating
  const renderStarRating = (rating: number, onChange: (rating: number) => void) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className={`${
              star <= rating ? 'text-yellow-400' : 'text-gray-300'
            } hover:text-yellow-400 transition-colors`}
          >
            <Star size={20} fill="currentColor" />
          </button>
        ))}
        <span className="ml-2 text-sm text-gray-600">({rating}/5)</span>
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informações Básicas */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Informações Básicas</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Fornecedor *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Segmento *
                </label>
                <input
                  type="text"
                  value={formData.segmento}
                  onChange={(e) => setFormData(prev => ({ ...prev, segmento: e.target.value }))}
                  placeholder="Ex: Materiais de Construção"
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contato *
                </label>
                <input
                  type="text"
                  value={formData.contato}
                  onChange={(e) => setFormData(prev => ({ ...prev, contato: e.target.value }))}
                  placeholder="Telefone/Email"
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CNPJ
                </label>
                <input
                  type="text"
                  value={formData.cnpj}
                  onChange={(e) => setFormData(prev => ({ ...prev, cnpj: e.target.value }))}
                  placeholder="00.000.000/0000-00"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Endereço
              </label>
              <textarea
                value={formData.endereco}
                onChange={(e) => setFormData(prev => ({ ...prev, endereco: e.target.value }))}
                rows={2}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Avaliação */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Avaliação e Desempenho</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confiabilidade
                </label>
                {renderStarRating(formData.confiabilidade, (rating) => 
                  setFormData(prev => ({ ...prev, confiabilidade: rating }))
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tempo Médio de Entrega (dias)
                </label>
                <input
                  type="number"
                  value={formData.tempoEntregaMedio}
                  onChange={(e) => setFormData(prev => ({ ...prev, tempoEntregaMedio: Number(e.target.value) }))}
                  min="0"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preço Médio (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.precoMedio}
                  onChange={(e) => setFormData(prev => ({ ...prev, precoMedio: Number(e.target.value) }))}
                  min="0"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Status</h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) => setFormData(prev => ({ ...prev, ativo: e.target.checked }))}
                    className="mr-2"
                  />
                  Fornecedor Ativo
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.bloqueado}
                    onChange={(e) => setFormData(prev => ({ ...prev, bloqueado: e.target.checked }))}
                    className="mr-2"
                  />
                  Fornecedor Bloqueado
                </label>
              </div>

              {formData.bloqueado && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo do Bloqueio
                  </label>
                  <input
                    type="text"
                    value={formData.motivoBloqueio}
                    onChange={(e) => setFormData(prev => ({ ...prev, motivoBloqueio: e.target.value }))}
                    placeholder="Descreva o motivo do bloqueio"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações
            </label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {initialData ? 'Atualizar' : 'Criar'} Fornecedor
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default FornecedorForm
